import { useCallback, useEffect, useState } from 'react';

/** What the recorder page's microphone check needs of the controller. */
export interface MicrophoneController {
  prepareMicrophone(deviceId: string): Promise<void>;
  cancelMicrophoneTest(): void;
}

/** The part of the controller's microphone state the switch reads. */
export interface MicrophoneState {
  kind: string;
  deviceId?: string;
}

/**
 * The recorder page's microphone, off until somebody turns it on (Jason, 24 Sep).
 *
 * The page used to open the selected microphone the moment it loaded, to drive the
 * level meter, and kept it open for as long as the page was showing - so visiting the
 * recorder to look at something else lit the browser's microphone indicator. It is a
 * switch now:
 *
 * - On opens the chosen microphone for the meter, and opens the new one when the
 *   choice changes. A walkthrough cannot start without it, as before.
 * - Off, or leaving the recorder's tab, closes it.
 * - A recording takes the microphone for itself, and the switch goes off, so the
 *   check does not reopen it the moment the recording ends.
 */
export function useMicrophoneSwitch(options: {
  controller: MicrophoneController;
  microphone: MicrophoneState;
  deviceId: string;
  /** A recording is starting, running or stopping. */
  busy: boolean;
  /** The recorder is what is on screen; the history tab is not. */
  showing: boolean;
}): { micOn: boolean; toggle: () => void } {
  const { controller, microphone, deviceId, busy, showing } = options;
  const [micOn, setMicOn] = useState(false);
  const ready = microphone.kind === 'ready' && microphone.deviceId === deviceId;

  // A recording, or another tab, turns the check off.
  useEffect(() => {
    if (busy || !showing) setMicOn(false);
  }, [busy, showing]);

  // Off closes it. The controller will not close a microphone a recording holds.
  useEffect(() => {
    if (!micOn) controller.cancelMicrophoneTest();
  }, [controller, micOn]);

  // On opens the chosen microphone, and the new one when the choice changes.
  useEffect(() => {
    if (!micOn || !deviceId || busy) return;
    if (microphone.kind === 'requesting' || ready) return;
    // A microphone that refused or failed stays that way until somebody asks again,
    // rather than being asked over and over by this effect.
    if (microphone.kind === 'denied' || microphone.kind === 'error' || microphone.kind === 'unavailable') return;
    void controller.prepareMicrophone(deviceId);
  }, [busy, controller, deviceId, micOn, microphone.kind, ready]);

  const toggle = useCallback(() => {
    if (micOn && ready) {
      setMicOn(false);
      return;
    }
    setMicOn(true);
    // Asked again after a refusal or a failure: the effect above waits for a change.
    if (micOn && deviceId) void controller.prepareMicrophone(deviceId);
  }, [controller, deviceId, micOn, ready]);

  return { micOn, toggle };
}
